<script lang="ts">
import { formatToolCall } from '@/utils/chat';
import {
  iButton,
  iButtonStyles,
  iButtonTypes,
  iButtonSizes,
  iIcon,
  iSpinner,
} from '@inscribe/signal';
import { ref } from 'vue';

export default {
  name: 'ChatMessageToolCall',
  components: { iButton, iSpinner, iIcon },
  props: {
    message: {
      type: Object,
      required: true,
    },
  },
  watch: {
    message: {
      handler() {
        this.evaluateContentHeight();
      },
      deep: true,
      immediate: true,
    },
  },
  setup() {
    const panelIsExpanded = ref(false);
    const panelIsFullText = ref(false);
    const COLLAPSED_HEIGHT = 150;

    let toolComponentHeight = ref(0);
    let contentHeight = ref(0);
    const contentElement = ref<HTMLElement | null>(null);

    return {
      formatToolCall,
      panelIsExpanded,
      panelIsFullText,
      iButtonStyles,
      iButtonTypes,
      iButtonSizes,
      contentElement,
      contentHeight,
      toolComponentHeight,
      COLLAPSED_HEIGHT,
      contentResizeObserver: null,
    };
  },

  computed: {
    toolIsLoading() {
      return !this.message.result;
    },
    toggleHeightButtonIsVisible() {
      return this.contentHeight > this.COLLAPSED_HEIGHT;
    },
  },
  mounted() {
    this.setupContentResizeObserver();
  },
  unmounted() {
    this.contentResizeObserver.disconnect();
  },
  methods: {
    setupContentResizeObserver() {
      this.contentResizeObserver = new ResizeObserver(() => {
        this.evaluateContentHeight();
      });
      this.contentResizeObserver.observe(this.$refs.contentElement);
    },
    togglePanel() {
      if (this.toolIsLoading) return;
      this.panelIsExpanded = !this.panelIsExpanded;
    },
    toggleFullText() {
      this.panelIsFullText = !this.panelIsFullText;
    },
    evaluateContentHeight() {
      this.$nextTick(() => {
        const content = this.$refs.contentElement;
        if (content) {
          this.contentHeight = content.scrollHeight;
          let contentReferenceHeight = this.panelIsFullText
            ? this.contentHeight
            : Math.min(this.contentHeight, this.COLLAPSED_HEIGHT);
          this.toolComponentHeight =
            contentReferenceHeight +
            (this.toggleHeightButtonIsVisible ? 104 : 68);
        }
      });
    },
  },
};
</script>

<template>
  <div
    :class="{ expanded: panelIsExpanded, loading: toolIsLoading }"
    :style="{
      height: panelIsExpanded ? `${toolComponentHeight}px` : 'auto',
    }"
    class="chat-message-tool i-p-2 i-radius-2xl"
  >
    <!-- header-->
    <button
      class="header d-flex align-items-center i-gap-4 i-p-3"
      @click="togglePanel"
    >
      <div class="icon-container">
        <i-spinner v-if="toolIsLoading" size="xs"></i-spinner>
        <i-icon
          v-else
          :symbol="
            panelIsExpanded ? 'medium-chevron-down' : 'medium-chevron-right'
          "
          :size="16"
        ></i-icon>
      </div>
      <div class="title d-flex align-items-center i-text-button-3">
        {{ formatToolCall(message.toolName, message.args).title }}
      </div>
    </button>

    <!-- body -->
    <div class="body d-flex flex-column i-radius-xl i-p-6">
      <!-- content -->
      <div
        :style="{
          maxHeight: panelIsFullText
            ? `${contentHeight}px`
            : `${COLLAPSED_HEIGHT}px`,
        }"
        class="content d-flex flex-column i-gap-5"
        ref="contentElement"
      >
        <div class="description i-text-body-3">
          {{ formatToolCall(message.toolName, message.args).fullDescription }}
        </div>

        <div class="result">
          <code class="i-text-code-3">{{ message.result }}</code>
        </div>
      </div>

      <!-- view more button -->
      <i-button
        v-if="toggleHeightButtonIsVisible"
        :size="iButtonSizes.SMALL"
        :type="iButtonTypes.NEUTRAL"
        :button-style="iButtonStyles.OUTLINE"
        :label="panelIsFullText ? 'View Less' : 'View More'"
        class="view-more-button i-mt-4"
        @button-click="toggleFullText"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.chat-message-tool {
  background-color: var(--surface-neutral);
  overflow: hidden;
  transition: height 150ms ease-in-out;

  &.expanded {
    .body {
      display: flex;
    }
  }

  &.loading {
    .header {
      cursor: default;
    }
  }

  .header {
    width: 100%;
    border: none;
    background-color: var(--surface-neutral);
    cursor: pointer;

    .icon-container {
      width: 16px;
      .i-icon {
        color: var(--text-neutral-bold);
      }
    }

    .title {
      height: 24px;
      color: var(--text-neutral-bold);
    }
  }

  .body {
    display: none;
    background-color: var(--base);

    .content {
      overflow: hidden;
      transition: max-height 150ms ease-in-out;
    }

    .result {
      background-color: var(--surface-neutral);

      code {
        font-family: 'Roboto Mono', monospace;
        font-size: 12px;
        font-style: normal;
        font-weight: 400;
        line-height: 16px; /* 133.333% */
        letter-spacing: -0.12px;
      }
    }

    .view-more-button {
      width: 90px;
    }
  }
}
</style>
